'use client';

import {
  CardAvatar,
  CardListEmpty,
  CardRow,
} from '@/components/data-table/card-list';
import {
  ClearFiltersButton,
  MultiSelectFilter,
  SearchInput,
} from '@/components/data-table/filters';
import { PaginationFooter } from '@/components/data-table/PaginationFooter';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useTenantSlug } from '@/hooks/useTenantSlug';
import {
  LINE_STATUS_OPTIONS,
  LINE_TYPE_OPTIONS,
  OPERATOR_OPTIONS,
} from '@/lib/filter-options';
import { formatEuros } from '@/lib/format';
import { lineMapping } from '@/lib/lineMapping';
import { simMapping } from '@/lib/simMapping';
import { statusMapping } from '@/lib/statusMapping';
import type { MobileLine, OrgBillingLine } from '@octave/contracts';
import { Phone, User2, X } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface LineFilters {
  status?: string[];
  operator?: string[];
  type?: string[];
  plan?: string[];
}

interface DataTableProps {
  data: MobileLine[];
  pagination?: {
    page: number;
    limit: number;
  };
  pageInfo?: {
    nextPage: number | null;
    previousPage: number | null;
    count: number;
    nbPage: number;
    page: number;
  };
  onPaginationChange?: (pagination: { page: number; limit: number }) => void;
  onFiltersChange?: (filters: LineFilters) => void;
  onSearchChange?: (searchText: string) => void;
  currentFilters?: LineFilters;
  currentSearchText?: string;
  isFetching?: boolean;
  availableFilters?: {
    status?: string[];
    operator?: string[];
    type?: string[];
  };
  /** Per-line billed totals (latest period) keyed by line id, for the hors-forfait badge. */
  overageByLine?: Map<string, OrgBillingLine>;
}

function getStatusDotColor(status: string) {
  switch (status) {
    case 'used':
      return 'bg-emerald-500';
    case 'restricted':
    case 'restrictionInProgress':
      return 'bg-amber-500';
    case 'suspended':
    case 'suspensionInProgress':
      return 'bg-orange-500';
    case 'terminated':
    case 'terminationInProgress':
    case 'cancelled':
      return 'bg-red-500';
    default:
      return 'bg-slate-400';
  }
}

function getStatusBadgeClasses(status: string) {
  switch (status) {
    case 'used':
      return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    case 'restricted':
    case 'restrictionInProgress':
      return 'bg-amber-50 text-amber-700 border-amber-200';
    case 'suspended':
    case 'suspensionInProgress':
      return 'bg-orange-50 text-orange-700 border-orange-200';
    case 'terminated':
    case 'terminationInProgress':
    case 'cancelled':
      return 'bg-red-50 text-red-700 border-red-200';
    default:
      return 'bg-slate-50 text-slate-600 border-slate-200';
  }
}

const operatorImages: Record<string, string> = {
  Orange: '/images/operators/orange.webp',
  SFR: '/images/operators/sfr.svg',
  Bouygues: '/images/operators/bouygues.png',
};

export function DataTable({
  data,
  pagination,
  pageInfo,
  onPaginationChange,
  onFiltersChange,
  onSearchChange,
  currentFilters,
  currentSearchText,
  isFetching,
  availableFilters,
  overageByLine,
}: DataTableProps) {
  const visibleStatusOptions = availableFilters?.status
    ? LINE_STATUS_OPTIONS.filter((o) =>
        availableFilters.status!.includes(o.value),
      )
    : LINE_STATUS_OPTIONS;
  const visibleOperatorOptions = availableFilters?.operator
    ? OPERATOR_OPTIONS.filter((o) =>
        availableFilters.operator!.includes(o.value),
      )
    : OPERATOR_OPTIONS;
  const visibleTypeOptions = availableFilters?.type
    ? LINE_TYPE_OPTIONS.filter((o) => availableFilters.type!.includes(o.value))
    : LINE_TYPE_OPTIONS;

  const [localSearchText, setLocalSearchText] = useState(
    currentSearchText || ''
  );

  const navigate = useNavigate();
  const { tenantSlug } = useTenantSlug();

  const handleClearFilters = () => {
    setLocalSearchText('');
    onFiltersChange?.({});
    onSearchChange?.('');
  };

  const handleMultiFilterChange = (
    filterType: 'status' | 'type' | 'operator',
    value: string,
    checked: boolean
  ) => {
    const newFilters = {
      status: currentFilters?.status || [],
      operator: currentFilters?.operator || [],
      type: currentFilters?.type || [],
      plan: currentFilters?.plan || [],
    };

    newFilters[filterType] = checked
      ? [...newFilters[filterType], value]
      : newFilters[filterType].filter((v) => v !== value);

    onFiltersChange?.({
      status: newFilters.status.length > 0 ? newFilters.status : undefined,
      operator:
        newFilters.operator.length > 0 ? newFilters.operator : undefined,
      type: newFilters.type.length > 0 ? newFilters.type : undefined,
      plan: newFilters.plan.length > 0 ? newFilters.plan : undefined,
    });
  };

  const handleSearchChange = (value: string) => {
    setLocalSearchText(value);
    onSearchChange?.(value);
  };

  const hasActiveFilters =
    localSearchText ||
    (currentFilters?.status && currentFilters.status.length > 0) ||
    (currentFilters?.operator && currentFilters.operator.length > 0) ||
    (currentFilters?.type && currentFilters.type.length > 0) ||
    (currentFilters?.plan && currentFilters.plan.length > 0);

  return (
    <div className="space-y-5">
      {/* Filters bar */}
      <div className="flex items-center gap-3 flex-wrap">
        <SearchInput
          placeholder="Rechercher un numéro ou utilisateur..."
          value={localSearchText}
          onChange={handleSearchChange}
        />
        <MultiSelectFilter
          label="Statut"
          options={visibleStatusOptions}
          selected={currentFilters?.status || []}
          onToggle={(value, checked) =>
            handleMultiFilterChange('status', value, checked)
          }
        />
        <MultiSelectFilter
          label="Opérateur"
          options={visibleOperatorOptions}
          selected={currentFilters?.operator || []}
          onToggle={(value, checked) =>
            handleMultiFilterChange('operator', value, checked)
          }
        />
        <MultiSelectFilter
          label="Type"
          options={visibleTypeOptions}
          selected={currentFilters?.type || []}
          onToggle={(value, checked) =>
            handleMultiFilterChange('type', value, checked)
          }
        />
        {currentFilters?.plan?.map((planName) => (
          <span
            key={planName}
            className="inline-flex items-center gap-1.5 rounded-full bg-violet-50 px-3 py-1 text-xs font-medium text-violet-700"
          >
            Forfait : {planName}
            <button
              type="button"
              onClick={() =>
                onFiltersChange?.({
                  status: currentFilters?.status,
                  operator: currentFilters?.operator,
                  type: currentFilters?.type,
                  plan: (currentFilters?.plan || []).filter(
                    (p) => p !== planName
                  ),
                })
              }
              className="cursor-pointer text-violet-500 hover:text-violet-800"
              aria-label={`Retirer le filtre ${planName}`}
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
        {hasActiveFilters && <ClearFiltersButton onClick={handleClearFilters} />}
      </div>

      {/* Cards list */}
      <div className="relative">
        {isFetching && (
          <div className="absolute inset-0 bg-white/60 backdrop-blur-[2px] z-10 flex items-center justify-center rounded-2xl">
            <div className="flex items-center gap-2.5 text-sm text-slate-500 bg-white px-4 py-2 rounded-full shadow-sm">
              <div className="animate-spin h-4 w-4 border-2 border-emerald-500 border-t-transparent rounded-full" />
              Mise à jour...
            </div>
          </div>
        )}

        <div className="space-y-2">
          {data.length ? (
            data.map((line) => {
              const userName = line.identity
                ? `${line.identity.firstName} ${line.identity.lastName}`
                : null;
              const type = line.phonePlan.isDataOnly ? 'dataonly' : 'classic';
              const overage = overageByLine?.get(line.id);

              return (
                <CardRow
                  key={line.id}
                  onClick={() => navigate(`/o/${tenantSlug}/lines/${line.id}`)}
                >
                  <CardAvatar
                    initials={
                      userName
                        ? `${line.identity!.firstName?.charAt(0) ?? ''}${
                            line.identity!.lastName?.charAt(0) ?? ''
                          }`
                        : null
                    }
                    fallbackIcon={Phone}
                  />

                  {/* Main info */}
                  <div className="flex-1 min-w-0 grid grid-cols-1 sm:grid-cols-4 gap-x-4 items-center">
                    {/* Col 1 — Number + User */}
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-slate-900 tabular-nums tracking-wide">
                        {line.number}
                      </p>
                      {userName ? (
                        <p className="text-xs text-slate-500 truncate flex items-center gap-1 mt-0.5">
                          <User2 className="h-3 w-3 shrink-0" />
                          {userName}
                        </p>
                      ) : (
                        <p className="text-xs text-slate-400 mt-0.5">
                          Non attribuée
                        </p>
                      )}
                      {overage && overage.consumptionTotal > 0 && (
                        <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700">
                          Hors-forfait {formatEuros(overage.consumptionTotal)}
                        </span>
                      )}
                    </div>

                    {/* Col 2 — Operator logo + Plan + Type */}
                    <div className="min-w-0 hidden sm:flex items-center gap-2.5">
                      <TooltipProvider delayDuration={200}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <img
                              src={
                                operatorImages[line.phonePlan.operator.name] ||
                                '/images/operators/default.svg'
                              }
                              alt={line.phonePlan.operator.name}
                              className="h-6 w-6 object-contain shrink-0"
                            />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>{line.phonePlan.operator.name}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                      <div className="min-w-0">
                        <p className="text-sm text-slate-700 truncate">
                          {line.phonePlan.name}
                        </p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-xs text-slate-400">
                            {lineMapping[type] || type}
                          </span>
                          {line.simCard?.type && (
                            <>
                              <span className="text-slate-300">·</span>
                              <span className="text-xs text-slate-400">
                                {simMapping[line.simCard.type] ||
                                  line.simCard.type}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Col 3 — Site (CF) */}
                    <div className="min-w-0 hidden sm:block text-center">
                      <p className="text-xs text-slate-500 truncate">
                        {line.site.name}
                      </p>
                    </div>

                    {/* Col 4 — Status tag (desktop) */}
                    <div className="hidden sm:flex justify-end">
                      <span
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full border whitespace-nowrap ${getStatusBadgeClasses(line.status)}`}
                      >
                        <span
                          className={`h-1.5 w-1.5 rounded-full ${getStatusDotColor(line.status)}`}
                        />
                        {statusMapping[line.status] || line.status}
                      </span>
                    </div>
                  </div>

                  {/* Status dot (mobile only) */}
                  <div className="sm:hidden">
                    <TooltipProvider delayDuration={200}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span
                            className={`shrink-0 h-2.5 w-2.5 rounded-full block ${getStatusDotColor(line.status)}`}
                          />
                        </TooltipTrigger>
                        <TooltipContent side="left">
                          <p>{statusMapping[line.status] || line.status}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                </CardRow>
              );
            })
          ) : (
            <CardListEmpty icon={Phone} />
          )}
        </div>
      </div>

      <PaginationFooter
        page={pagination?.page || 1}
        pageCount={pageInfo?.nbPage || 1}
        summary={`${pageInfo?.count || data.length} lignes`}
        pageSize={pagination?.limit || 20}
        onPageSizeChange={(size) =>
          onPaginationChange?.({ page: 1, limit: size })
        }
        onPageChange={(page) =>
          pagination && onPaginationChange?.({ ...pagination, page })
        }
        isFetching={isFetching}
      />
    </div>
  );
}
